import { lazy, ComponentType } from "react";

type ComponentImport<T> = () => Promise<{ default: T }>;

// Track failed imports to allow cache-busting on retry
const failedImports = new Set<string>();

/**
 * Lazy load a component with automatic retry on failure.
 * Retries up to 3 times with exponential backoff before giving up.
 * On failure, adds cache-busting query parameter to bypass browser cache.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: ComponentImport<T>,
  retries = 3,
  interval = 1000
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;
    const importKey = importFn.toString();

    for (let i = 0; i < retries; i++) {
      try {
        // Try to import the component
        const component = await importFn();
        // Success - remove from failed imports if it was there
        failedImports.delete(importKey);
        return component;
      } catch (error) {
        lastError = error as Error;
        console.warn(`Failed to load chunk (attempt ${i + 1}/${retries}):`, error);

        // If this was the last attempt, don't wait
        if (i < retries - 1) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise((resolve) => setTimeout(resolve, interval * Math.pow(2, i)));
        }
      }
    }

    // Mark this import as failed for potential cache-busting later
    failedImports.add(importKey);
    
    // All retries failed - throw the last error
    throw lastError;
  });
}

/**
 * Clear the failed imports tracking.
 * Call this before attempting to reload the page.
 */
export function clearFailedImports(): void {
  failedImports.clear();
}
